<template>
    <div
        class="e-penalty-match JARVIS_MatchCard_View"
        v-bind:class="{ _live: match.isLive }"
        v-bind:data-testid="`e-penalty-card-${match.eventId}`"
    >
        <EPenaltyMatchInfo v-bind:match="match" />

        <!-- e-Penalty streams are only offered through Twitch and only shown once the match is live -->
        <div v-if="match.isLive && match.stream?.videoUrl" class="e-penalty-match-stream">
            <TwitchLiveStream v-bind:videoUrl="match.stream.videoUrl" v-bind:isAutoplay="false" />
        </div>

        <div v-if="hasMainMarketOdds || (isMoreMarketExpanded && moreMarketOddsGroups.length)" class="e-penalty-market notranslate">
            <EPenaltyMainMarket
                v-if="hasMainMarketOdds"
                v-bind:match="match"
                v-bind:mainMarketOdds="mainMarketOdds"
                v-bind:isExpanded="isMoreMarketExpanded"
            />
            <template v-if="isMoreMarketExpanded">
                <EPenaltyOptionMarket
                    v-for="(moreMarketOddsList, index) in moreMarketOddsGroups"
                    v-bind:key="`${moreMarketOddsList[0].marketGroup.id}:${moreMarketOddsList[0].marketType}`"
                    v-bind:match="match"
                    v-bind:oddsList="moreMarketOddsList"
                    v-bind:marketOrder="index + 1"
                />
            </template>
        </div>

        <MoreMarketToggle
            v-if="moreMarketCount > 0"
            v-model:isExpanded="isMoreMarketExpanded"
            v-bind:count="moreMarketCount"
            v-bind:isLive="match.isLive"
        />
    </div>
</template>

<script lang="ts" setup>
    import sortBy from 'lodash/sortBy';
    import { storeToRefs } from 'pinia';
    import { type PropType, computed, ref, toRefs } from 'vue';
    import MoreMarketToggle from '@/components/common/MoreMarketToggle.vue';
    import TwitchLiveStream from '@/components/match/TwitchLiveStream.vue';
    import EPenaltyMainMarket from '@/components/match/ePenalty/EPenaltyMainMarket.vue';
    import EPenaltyMatchInfo from '@/components/match/ePenalty/EPenaltyMatchInfo.vue';
    import EPenaltyOptionMarket from '@/components/match/ePenalty/EPenaltyOptionMarket.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { MarketType } from '@/interface/enum';
    import { groupMoreMarketOddsForDisplay } from '@/libraries/composables/useMoreMarket';
    import {
        ePenaltyMainMarketBetTypeGroups,
        ePenaltyMainMarketConfig,
        ePenaltyMoreMarketDisplayOrder,
    } from '@/libraries/ePenaltyMarketRule';
    import { useOdds } from '@/libraries/oddsApi/composables/useOdds';
    import {
        calculateMoreMarketCountByConfig,
        getMainMarketOddsList,
        isMarketInConfig,
    } from '@/libraries/oddsDisplay';
    import { useRootStore } from '@/stores/rootStore';
    import { EnumPresetFilterType } from '@/types';

    const props = defineProps({
        match: {
            type: Object as PropType<IMatch>,
            required: true,
        },
    });

    const { match } = toRefs(props);
    const { isMixParlayMode } = storeToRefs(useRootStore());

    const isMoreMarketExpanded = ref(false);
    const isLive = computed(() => match.value.isLive);
    const presetFilter = computed(() => (isLive.value ? EnumPresetFilterType.Live : EnumPresetFilterType.NonLive));
    const betTypeGroupIds = computed(() => (isMoreMarketExpanded.value ? null : ePenaltyMainMarketBetTypeGroups));

    const { oddsList } = useOdds({
        eventId: match.value.eventId,
        betTypeGroupIds,
        isMixParlayMode,
        presetFilter,
        isLive,
        isEnabled: true,
        sportType: match.value.sportType,
        leagueSize: match.value.leagueSize,
    });

    const mainMarketOdds = computed(() => getMainMarketOddsList(oddsList.value ?? [], ePenaltyMainMarketConfig).flat());
    const hasMainMarketOdds = computed(() => mainMarketOdds.value.some(market => market.rawOddsList.length > 0));

    const moreMarketOddsGroups = computed(() => sortBy(
        groupMoreMarketOddsForDisplay(
            (oddsList.value ?? []).filter(odds => !isMarketInConfig(ePenaltyMainMarketConfig, odds.marketGroup.id, odds.marketType)),
        ),
        group => toDisplayRank(group[0].marketType),
    ));

    function toDisplayRank(marketType: MarketType) {
        const rank = ePenaltyMoreMarketDisplayOrder.indexOf(marketType);
        return rank === -1 ? ePenaltyMoreMarketDisplayOrder.length : rank;
    }

    const moreMarketCount = computed(() => calculateMoreMarketCountByConfig(ePenaltyMainMarketConfig, match.value.marketTypeCount));
</script>

<style lang="scss" scoped>
@import "@/scss/components/ePenalty/ePenaltyMatch";
</style>
